import { FC, ReactNode, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Provider, useAtomValue, useSetAtom } from 'jotai';
import { useForm } from '@tanstack/react-form';
import { InlineNotification, Loading } from '@carbon/react';

import { FullPageWizardLayout, StepConfig } from 'components/full-page-wizard';

import { AfeFormProvider } from './context';
import {
  afeDetailAtom,
  afeDetailLoadableAtom,
  afeEnumsLoadableAtom,
  afeWellIntersectionAtom,
  basinsLoadableAtom,
  countiesLoadableAtom,
  formationsLoadableAtom,
  statesLoadableAtom,
  wellQueryAtom,
} from './atoms';
import type { AfeFormInstance, AfeFormValues, AfeWizardMode } from './types';
import { INITIAL_FORM, afeDetailToFormValues } from './utils';
import { AfeTypeStep } from './steps/afe-type-step';
import { EntityStep } from './steps/entity-step';
import { SupportingInformationStep } from './steps/supporting-information-step';
import { WellInformationStep } from './steps/well-information-step';
import { CostInformationStep } from './steps/cost-information-step';
import { SummaryStep } from './steps/summary-step';
import { SourceDocumentBanner } from '../source-document-banner';
import type { AfeSourceDocument } from '../types';

/**
 * Route component for create and edit AFE wizard.
 * Detects mode from URL params, gates on data loading, then renders
 * AfeWizardContent once all required data is available.
 * @return {JSX.Element} Loading state, error, or the wizard content
 */
const AfeWizardPage: FC = () => {
  const { id } = useParams<{ id?: string }>();
  const mode: AfeWizardMode = id ? 'edit' : 'create';

  const enumsLoadable = useAtomValue(afeEnumsLoadableAtom);
  const triggerDetail = useSetAtom(afeDetailAtom);
  const detailLoadable = useAtomValue(afeDetailLoadableAtom);

  const triggerBasins = useSetAtom(basinsLoadableAtom);
  const triggerStates = useSetAtom(statesLoadableAtom);
  const triggerFormations = useSetAtom(formationsLoadableAtom);
  const triggerCounties = useSetAtom(countiesLoadableAtom);
  const triggerWellMap = useSetAtom(wellQueryAtom);
  const triggerIntersection = useSetAtom(afeWellIntersectionAtom);

  useEffect(() => {
    if (id) triggerDetail(id);
  }, [id, triggerDetail]);

  // Edit mode: preload dropdown options as soon as detail data arrives
  useEffect(() => {
    if (mode !== 'edit') return;
    if (detailLoadable.state !== 'hasData' || !detailLoadable.data) return;

    const detail = detailLoadable.data.data;
    const wi = detail.wellInformation;
    triggerBasins();
    triggerStates();
    if (wi.basinName) triggerFormations(wi.basinName);
    if (detail.stateId) triggerCounties(detail.stateId);
    // Edit mode opens on the Summary step, so the Well Information form — which
    // normally triggers these on UWI select — never mounts. Load the well's map
    // geometry and unit intersections here so the map can plot the well.
    if (detail.slotId) {
      triggerWellMap(detail.slotId);
      triggerIntersection(detail.slotId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, detailLoadable.state]);

  // Loading gate: enums must be ready for both modes
  if (enumsLoadable.state === 'loading') {
    return <Loading withOverlay={false} small description="Loading..." />;
  }

  if (enumsLoadable.state === 'hasError') {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        title="Failed to load AFE configuration"
      />
    );
  }

  // Edit mode: detail must also be ready
  if (mode === 'edit') {
    if (detailLoadable.state === 'loading') {
      return <Loading withOverlay={false} small description="Loading AFE..." />;
    }

    if (detailLoadable.state === 'hasError') {
      return (
        <InlineNotification
          kind="error"
          lowContrast
          title="Failed to load AFE details"
        />
      );
    }

    // state === 'hasData' but data is null before trigger fires
    if (!detailLoadable.data) {
      return <Loading withOverlay={false} small description="Loading AFE..." />;
    }
  }

  const defaultValues: AfeFormValues =
    mode === 'edit' && detailLoadable.state === 'hasData' && detailLoadable.data
      ? afeDetailToFormValues(detailLoadable.data.data)
      : INITIAL_FORM;

  const sourceDocument: AfeSourceDocument | null =
    mode === 'edit' && detailLoadable.state === 'hasData' && detailLoadable.data
      ? (detailLoadable.data.data.sourceDocument ?? null)
      : null;

  return (
    <AfeWizardContent
      mode={mode}
      afeId={id ?? null}
      defaultValues={defaultValues}
      sourceDocument={sourceDocument}
    />
  );
};

/**
 * Inner wizard component that creates the TanStack Form instance and
 * assembles the step configuration. Separated from AfeWizardPage to
 * avoid conditional useForm hook calls.
 * @param {object} props - Mode, AFE ID, and default form values
 * @param {AfeWizardMode} props.mode - create or edit
 * @param {string | null} props.afeId - AFE ID for edit mode
 * @param {AfeFormValues} props.defaultValues - Initial form values
 * @return {JSX.Element} The wizard wrapped in AfeFormProvider
 */
const AfeWizardContent: FC<{
  mode: AfeWizardMode;
  afeId: string | null;
  defaultValues: AfeFormValues;
  sourceDocument: AfeSourceDocument | null;
}> = ({ mode, afeId, defaultValues, sourceDocument }) => {
  const history = useHistory();

  const form = useForm({ defaultValues }) as unknown as AfeFormInstance;

  const banner =
    mode === 'edit' && sourceDocument ? (
      <SourceDocumentBanner sourceDocument={sourceDocument} />
    ) : null;

  // Banner pinned above every step's content. `FullPageWizard` only renders
  // the active step, so wrapping each `content` here is the supported way to
  // surface page-level chrome without forking the wizard layout.
  const withBanner = (content: ReactNode): ReactNode => (
    <>
      {banner}
      {content}
    </>
  );

  const steps: StepConfig[] = [
    { label: 'AFE Type*', content: withBanner(<AfeTypeStep />) },
    { label: 'Entity', content: withBanner(<EntityStep />) },
    {
      label: 'Well Information*',
      content: withBanner(<WellInformationStep />),
    },
    {
      label: 'Cost Information*',
      content: withBanner(<CostInformationStep />),
    },
    {
      label: 'Supporting Information',
      content: withBanner(<SupportingInformationStep />),
    },
    {
      label: 'Summary',
      content: withBanner(<SummaryStep />),
      nextLabel: 'Save',
      ...(mode === 'edit' && { isInitialStep: true }),
    },
  ];

  return (
    <AfeFormProvider form={form} mode={mode} afeId={afeId}>
      <FullPageWizardLayout
        breadcrumbs={[['/afe_catalog', 'AFE Catalog']]}
        title={mode === 'edit' ? 'Edit AFE' : 'Create AFE'}
        steps={steps}
        onCancel={() => history.push('/afe_catalog')}
      />
    </AfeFormProvider>
  );
};

/**
 * Wraps the wizard page in a scoped Jotai Provider so all atoms
 * are reset when navigating away and back.
 * @return {JSX.Element} Provider-scoped wizard page
 */
const ScopedAfeWizardPage: FC = () => (
  <Provider>
    <AfeWizardPage />
  </Provider>
);

export { ScopedAfeWizardPage as AfeWizardPage };
